import { useEffect } from "react";
import { Group, NumberInput, Stack, TextInput, Textarea, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { DigitsOnlyInput } from "@/components/DigitsOnlyInput";
import type { Company } from "@/types/api";

export interface CompanyFormValues {
  code: string;
  name: string;
  full_name: string;
  inn: string;
  kpp: string;
  ogrn: string;
  address: string;
  bank_account: string;
  bank_bik: string;
  bank_name: string;
  vat_default: number;
  signatory_name: string;
  signatory_position: string;
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  smtp_from_name: string;
  smtp_use_ssl: boolean;
}

export type CompanyFormApi = ReturnType<typeof useForm<CompanyFormValues>>;

const digits = (n: number) => (s: string) =>
  !s ? null : /^\d+$/.test(s) && s.length === n ? null : `Должно быть ${n} цифр`;
const innRule = (s: string) =>
  /^\d{10}$|^\d{12}$/.test(s) ? null : "ИНН: 10 или 12 цифр";
const ogrnRule = (s: string) =>
  !s || /^\d{13}$|^\d{15}$/.test(s) ? null : "ОГРН: 13 или 15 цифр";

export function CompanyForm({
  initial,
  onChange,
}: {
  initial?: Company;
  onChange: (api: CompanyFormApi) => void;
}) {
  const form = useForm<CompanyFormValues>({
    initialValues: {
      code: initial?.code ?? "",
      name: initial?.name ?? "",
      full_name: initial?.full_name ?? "",
      inn: initial?.inn ?? "",
      kpp: initial?.kpp ?? "",
      ogrn: initial?.ogrn ?? "",
      address: initial?.address ?? "",
      bank_account: initial?.bank_account ?? "",
      bank_bik: initial?.bank_bik ?? "",
      bank_name: initial?.bank_name ?? "",
      vat_default: initial?.vat_default ?? 20,
      signatory_name: initial?.signatory_name ?? "",
      signatory_position: initial?.signatory_position ?? "",
      smtp_host: initial?.smtp_host ?? "",
      smtp_port: initial?.smtp_port ?? 465,
      smtp_username: initial?.smtp_username ?? "",
      smtp_password: "",  // пароль не отдаётся на фронт; для смены — ввести новый
      smtp_from_name: initial?.smtp_from_name ?? "",
      smtp_use_ssl: initial?.smtp_use_ssl ?? true,
    },
    validate: {
      code: (v) => (v.trim() ? null : "Обязательно"),
      name: (v) => (v.trim() ? null : "Обязательно"),
      full_name: (v) => (v.trim() ? null : "Обязательно"),
      inn: innRule,
      kpp: digits(9),
      ogrn: ogrnRule,
      bank_bik: digits(9),
    },
  });

  // Без deps — Mantine useForm возвращает новый объект формы на каждый рендер
  // (values читаются из state, объект пересоздаётся). Родитель должен хранить
  // formApi в useRef, иначе при submit увидит initial values.
  // См. длинный коммент в DriverForm.tsx и Fix #8.
  useEffect(() => {
    onChange(form);
  });

  return (
    <Stack gap="md">
      <Group grow>
        <TextInput label="Код" required {...form.getInputProps("code")} />
        <NumberInput label="НДС, %" {...form.getInputProps("vat_default")} min={0} max={30} />
      </Group>
      <TextInput label="Название" required {...form.getInputProps("name")} />
      <TextInput label="Полное название" required {...form.getInputProps("full_name")} />
      <Group grow>
        <TextInput label="ИНН" required {...form.getInputProps("inn")} />
        <TextInput label="КПП" {...form.getInputProps("kpp")} />
        <TextInput label="ОГРН" {...form.getInputProps("ogrn")} />
      </Group>
      <Textarea label="Адрес" {...form.getInputProps("address")} />

      <Title order={5} mt="md">Банковские реквизиты</Title>
      <Group grow>
        <DigitsOnlyInput
          label="Расчётный счёт"
          placeholder="20 цифр"
          maxLength={20}
          value={form.values.bank_account}
          onChange={(v) => form.setFieldValue("bank_account", v)}
          error={form.errors.bank_account}
        />
        <DigitsOnlyInput
          label="БИК"
          placeholder="9 цифр"
          maxLength={9}
          value={form.values.bank_bik}
          onChange={(v) => form.setFieldValue("bank_bik", v)}
          error={form.errors.bank_bik}
        />
      </Group>
      <TextInput label="Банк" {...form.getInputProps("bank_name")} />

      <Title order={5} mt="md">Подписант</Title>
      <Group grow>
        <TextInput label="ФИО" {...form.getInputProps("signatory_name")} />
        <TextInput label="Должность" {...form.getInputProps("signatory_position")} />
      </Group>

      {/* SMTP настройки — для отправки заявок с этой фирмы.
          Пароль хранится в БД; на GET не приходит, на UPDATE передаётся
          только если ввели новый. */}
      <Title order={5} mt="md">Отправка почты (SMTP)</Title>
      <Group grow>
        <TextInput
          label="SMTP сервер"
          placeholder="smtp.mail.ru"
          {...form.getInputProps("smtp_host")}
        />
        <NumberInput
          label="Порт"
          min={1}
          max={65535}
          {...form.getInputProps("smtp_port")}
        />
      </Group>
      <Group grow>
        <TextInput
          label="Логин (email)"
          placeholder="info@euromarket.ru"
          {...form.getInputProps("smtp_username")}
        />
        <TextInput
          label="Пароль (для смены — введите новый)"
          type="password"
          placeholder={initial?.smtp_host ? "•••••• (не менять)" : ""}
          {...form.getInputProps("smtp_password")}
        />
      </Group>
      <Group grow>
        <TextInput
          label="Имя отправителя"
          placeholder="ЕвроМаркет"
          {...form.getInputProps("smtp_from_name")}
        />
        <TextInput
          label="SSL/TLS"
          disabled
          value={form.values.smtp_use_ssl ? "SSL (465, mail.ru)" : "STARTTLS (587)"}
        />
      </Group>
    </Stack>
  );
}
